import {Button} from '@nlabs/gothamjs';

import ufatPublishingPagesHero from '../../assets/ufat-publishing-pages-hero.png';
import {Hero} from '../../components/Hero';
import {UFATLogoIcon} from '../../components/UFATLogoIcon';
import {Page} from '../../components/Page';
import {Section} from '../../components/Section';
import {navigate} from '../../lib/navigation';
import {STORE_URL} from '../../lib/site';
import {button, buttonLight, buttonOutlineDark, card, cardContent, cardTitle, heroActionLinkDark, paragraphMuted} from '../../lib/styles';
import {Route} from '../../types/routes';

const quickFacts = [
  ['Creator Share', '70%'],
  ['NSI Share', '30%'],
  ['Rights Term', '3 Years'],
  ['Copyright Ownership', 'Creator Retains Ownership'],
  ['Distribution', 'Worldwide Print & Digital'],
  ['Payments', 'Quarterly'],
  ['Design Services', 'Included'],
  ['Audiobook Rights', 'Available']
];

const publishingServices = [
  'Professional publishing and distribution',
  'Print and digital book production',
  'Distribution through major retailers and marketplaces',
  'Marketing and promotional support',
  'ISBN registration and publishing management',
  'Optional cover design and interior layout services at no additional publishing fee'
];

const distributionChannels = [
  {key: 'amazon', label: 'Amazon'},
  {key: 'kindle', label: 'Kindle'},
  {key: 'apple-books', label: 'Apple Books'},
  {key: 'ingram', label: 'Ingram Distribution'},
  {key: 'ufat', label: 'UFAT Studios online and physical storefronts'},
  {key: 'channels', label: 'Additional distribution channels as opportunities arise'}
];

const publishingFormats = [
  'eBooks',
  'Paperback editions',
  'Hardcover editions',
  'Audiobooks',
  'Future digital publishing formats'
];

const publishingFeatureColumns = [
  {
    body: 'Professional publishing management for manuscripts, metadata, ISBN registration, catalog planning, and long-term retail readiness.',
    detail: 'We organize the publishing path so creators can focus on the work while UFAT Studios manages production standards and marketplace preparation.',
    title: 'Publishing management'
  },
  {
    body: 'Print and digital production across manuscript preparation, interior layout, optional cover design, and format-specific delivery.',
    detail: 'Paperback, hardcover, ebook, and future format planning are handled as part of a consistent production process.',
    title: 'Book production'
  },
  {
    body: 'Marketplace and storefront access through major print and digital channels, including Amazon, Kindle, Apple Books, and Ingram.',
    detail: 'We position approved works for worldwide discovery while keeping the catalog presentation clear, polished, and expandable.',
    title: 'Distribution'
  },
  {
    body: 'Launch support, promotional coordination, reader-facing copy, storefront visibility, and ongoing catalog presentation.',
    detail: 'Marketing support is shaped around the book, the creator, and the audience the work is built to reach.',
    title: 'Marketing support'
  }
];

const DetailCard = ({body, eyebrow, title}: {body: string; eyebrow: string; title: string}) => (
  <div className={`${card} bg-white/82`}>
    <div className={`${cardContent} grid gap-3`}>
      <p className="m-0 text-[.75rem] font-extrabold uppercase text-[var(--nx-blue)]">{eyebrow}</p>
      <h3 className={cardTitle}>{title}</h3>
      <p className={paragraphMuted}>{body}</p>
    </div>
  </div>
);

const DistributionLogo = ({channel}: {channel: string}) => {
  if(channel === 'amazon') {
    return (
      <span className="relative inline-flex h-12 items-center pr-3 text-[1.25rem] font-extrabold tracking-[-.02em] text-[var(--nx-ink)]">
        amazon
        <span className="absolute bottom-1 left-7 h-3 w-12 rounded-b-full border-b-2 border-[#ff9900]" />
      </span>
    );
  }

  if(channel === 'kindle') {
    return <span className="inline-flex h-12 items-center text-[1.35rem] font-light tracking-[.04em] text-[#1a5f8f]">kindle</span>;
  }

  if(channel === 'apple-books') {
    return (
      <span className="inline-flex h-12 items-center gap-2 text-[1.05rem] font-extrabold text-[var(--nx-ink)]">
        <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-[linear-gradient(135deg,#ff8b2b,#f74752)] text-white">
          <span className="h-5 w-5 rounded-sm border-2 border-white border-t-0" />
        </span>
        Apple Books
      </span>
    );
  }

  if(channel === 'ingram') {
    return <span className="inline-flex h-12 items-center text-[1.05rem] font-extrabold uppercase tracking-[.18em] text-[#2a4968]">Ingram</span>;
  }

  if(channel === 'ufat') {
    return (
      <span className="inline-flex h-12 items-center gap-2 text-[1.05rem] font-extrabold text-[var(--nx-ink)]">
        <span className="inline-flex h-10 w-10 text-[var(--nx-ink)]"><UFATLogoIcon /></span>
        UFAT
      </span>
    );
  }

  return (
    <span className="inline-flex h-12 items-center gap-2 text-[1.02rem] font-extrabold text-[var(--nx-ink)]">
      <span className="grid h-9 w-9 place-items-center rounded-full border border-[rgba(16,22,36,.16)] bg-white text-[1.2rem]">+</span>
      Channels
    </span>
  );
};

export const Publishing = ({route}: {route: Route}) => (
  <Page route={route}>
    <Hero
      actions={(
        <>
          <a className={heroActionLinkDark} href={STORE_URL}>Visit Store</a>
          <Button className={buttonLight} onClick={() => navigate('/creators')}>Publish With Us</Button>
        </>
      )}
      backgroundImage={ufatPublishingPagesHero}
      imageLabel="Publishing placeholder image"
      kicker="UFAT Studios, Inc."
      subtitle="We partner with authors, creators, and storytellers to bring exceptional works to readers worldwide through a straightforward, transparent, and creator-focused publishing model."
      title="Publishing With UFAT Studios"
      tone="gold"
    />
    <Section className="nx-section-publishing" title="A creator-focused publishing model">
      <div className="grid gap-[clamp(28px,5vw,64px)]">
        <div className="grid items-start gap-[clamp(26px,4vw,54px)] min-[960px]:grid-cols-[minmax(0,1fr)_minmax(320px,.62fr)]">
          <div className="grid max-w-[860px] gap-5">
            <p className="m-0 text-[clamp(1.2rem,2vw,1.65rem)] font-bold leading-[1.45] text-[var(--nx-ink)]">
              At UFAT Studios, we partner with authors, creators, and storytellers to bring exceptional works to readers worldwide.
            </p>
            <p className="m-0 text-[clamp(1.02rem,1.24vw,1.2rem)] leading-[1.82] text-[rgba(52,64,84,.86)]">
              Our publishing model is designed to be straightforward, transparent, and creator-focused, giving creators a professional path to print, digital, retail, marketing, and long-term catalog growth.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button className={button} onClick={() => navigate('/creators')}>Submit Your Manuscript</Button>
              <a className={buttonOutlineDark} href={STORE_URL}>Explore Storefront</a>
            </div>
          </div>
          <div className="grid gap-3 rounded-[var(--nx-radius)] border border-[rgba(16,22,36,.08)] bg-white/74 p-[clamp(18px,3vw,28px)] shadow-[0_24px_80px_rgba(16,22,36,.08)] backdrop-blur">
            <p className="m-0 text-[.78rem] font-extrabold uppercase text-[var(--nx-blue)]">Quick facts</p>
            <div className="grid gap-2">
              {quickFacts.map(([label, value]) => (
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-[var(--nx-radius)] border border-[rgba(16,22,36,.08)] bg-white/76 px-4 py-3" key={label}>
                  <span className="text-sm font-bold text-[rgba(52,64,84,.82)]">{label}</span>
                  <span className="text-right text-sm font-extrabold text-[var(--nx-ink)]">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="grid gap-[clamp(28px,4vw,56px)] border-t border-[rgba(16,22,36,.12)] pt-[clamp(34px,5vw,64px)] min-[900px]:grid-cols-[.82fr_repeat(3,1fr)]">
          <div className="grid content-start gap-6">
            <h3 className="m-0 text-[clamp(2.1rem,4vw,3.35rem)] font-extrabold leading-none text-[var(--nx-ink)]">Services</h3>
            <p className="m-0 max-w-[320px] text-[clamp(1.02rem,1.3vw,1.18rem)] leading-[1.72] text-[rgba(52,64,84,.82)]">
              Everything needed to prepare, publish, distribute, and support exceptional books under the UFAT Studios catalog.
            </p>
            <Button className="inline-flex min-h-12 w-fit cursor-pointer items-center justify-center rounded-[var(--nx-radius)] border border-[var(--nx-ink)] bg-[var(--nx-ink)] px-[22px] font-extrabold text-white" onClick={() => navigate('/creators')}>All services</Button>
          </div>
          {publishingFeatureColumns.slice(1).map((service) => (
            <article className="grid content-start gap-6" key={service.title}>
              <h3 className="m-0 text-[clamp(1.5rem,2.2vw,2.1rem)] font-extrabold leading-tight text-[var(--nx-ink)]">{service.title}</h3>
              <p className="m-0 text-[clamp(1.02rem,1.35vw,1.2rem)] leading-[1.72] text-[rgba(52,64,84,.86)]">{service.body}</p>
              <p className="m-0 text-[clamp(.98rem,1.16vw,1.08rem)] leading-[1.72] text-[rgba(52,64,84,.74)]">{service.detail}</p>
              <button className="w-fit border-b border-dotted border-[rgba(52,64,84,.48)] bg-transparent px-0 pb-2 text-[1.02rem] font-semibold text-[rgba(52,64,84,.78)]" onClick={() => navigate('/creators')} type="button">Explore service</button>
            </article>
          ))}
        </div>
      </div>
    </Section>
    <Section className="nx-section-projects-alt" title="What We Provide">
      <div className="grid gap-4 min-[760px]:grid-cols-2">
        {publishingServices.map((service) => (
          <div className="flex gap-4 rounded-[var(--nx-radius)] border border-[var(--nx-line)] bg-white p-5" key={service}>
            <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--nx-blue)] text-xs font-extrabold text-white">✓</span>
            <p className="m-0 text-[1.02rem] leading-[1.65] text-[rgba(52,64,84,.88)]">{service}</p>
          </div>
        ))}
      </div>
    </Section>
    <Section className="nx-section-publishing" title="Revenue, Rights, and Transparency">
      <div className="grid gap-[18px] min-[841px]:grid-cols-3">
        <DetailCard
          body="Creators receive 70% of net publishing revenue, and UFAT Studios receives 30%. Payments are issued quarterly, with statements provided for transparency."
          eyebrow="Revenue share"
          title="70 / 30 creator model"
        />
        <DetailCard
          body="UFAT Studios receives exclusive publishing rights for a term of three years. Creators retain ownership of their intellectual property and copyrights."
          eyebrow="Publishing rights"
          title="Three-year term"
        />
        <DetailCard
          body="All works are reviewed prior to publication to ensure they align with UFAT Studios quality, production, and publishing standards."
          eyebrow="Creative standards"
          title="Curated quality"
        />
      </div>
    </Section>
    <Section className="nx-section-projects-alt" title="Distribution">
      <div className="grid items-start gap-[clamp(24px,4vw,48px)] min-[900px]:grid-cols-[.8fr_1.2fr]">
        <div className="grid gap-4">
          <p className="m-0 text-[clamp(1.35rem,2.5vw,2rem)] font-bold leading-[1.3] text-[var(--nx-ink)]">Published works may be distributed through major print and digital channels.</p>
          <p className={paragraphMuted}>UFAT Studios manages the publishing path across marketplace listings, distribution opportunities, storefront presence, and future channels as they become available.</p>
        </div>
        <div className="grid gap-3 min-[620px]:grid-cols-2">
          {distributionChannels.map((channel) => (
            <div className="grid min-h-[128px] content-between gap-4 rounded-[var(--nx-radius)] border border-[var(--nx-line)] bg-white p-5" key={channel.key}>
              <DistributionLogo channel={channel.key} />
              <span className="text-[1.02rem] font-extrabold leading-[1.35] text-[var(--nx-ink)]">{channel.label}</span>
            </div>
          ))}
        </div>
      </div>
    </Section>
    <Section className="nx-dark" title="Audio & Future Formats">
      <div className="grid items-start gap-[clamp(28px,5vw,64px)] min-[900px]:grid-cols-[minmax(0,.8fr)_minmax(0,1.2fr)]">
        <div className="grid gap-4">
          <p className="m-0 text-[clamp(1.2rem,2vw,1.65rem)] font-bold leading-[1.45] text-white">UFAT Studios may publish approved works in additional formats as the catalog expands.</p>
          <p className="m-0 leading-[1.75] text-white/70">Creators agree not to publish, sell, distribute, or upload the work through other publishers, retailers, or platforms during the exclusivity period so distribution and marketing efforts stay consistent.</p>
        </div>
        <div className="grid gap-3 min-[620px]:grid-cols-2">
          {publishingFormats.map((format) => (
            <div className="rounded-[var(--nx-radius)] border border-white/15 bg-white/[.06] p-5 text-[1.05rem] font-extrabold text-white" key={format}>{format}</div>
          ))}
        </div>
      </div>
    </Section>
    <Section className="nx-section-publishing" title="Ready to Publish?">
      <div className="grid max-w-[920px] gap-5">
        <p className="m-0 text-[clamp(1.2rem,2vw,1.65rem)] font-bold leading-[1.45] text-[var(--nx-ink)]">
          Whether you are a first-time author or an established creator, UFAT Studios provides the tools, distribution, and support needed to bring your work to a global audience.
        </p>
        <p className="m-0 text-[clamp(1.02rem,1.24vw,1.2rem)] leading-[1.82] text-[rgba(52,64,84,.86)]">
          Submit your manuscript today and begin your publishing journey with UFAT Studios.
        </p>
        <div className="flex flex-wrap gap-3 pt-2">
          <Button className={button} onClick={() => navigate('/creators')}>Submit Your Manuscript</Button>
          <Button className={buttonOutlineDark} onClick={() => navigate('/support')}>Ask a Question</Button>
        </div>
      </div>
    </Section>
  </Page>
);
